import { QuadRenderer } from "@monogrid/gainmap-js";
import {
	type EncodingParametersBase,
	encode,
	findTextureMinMax,
} from "@monogrid/gainmap-js/encode";
import GainMapWorker from "@monogrid/gainmap-js/worker?worker";
import {
	getPromiseWorker,
	getWorkerInterface,
} from "@monogrid/gainmap-js/worker-interface";
import { Result, ResultAsync } from "neverthrow";
import type { DataTexture } from "three";
import { HDRLoader } from "three/addons/loaders/HDRLoader.js";

const encodeThrowable = Result.fromThrowable(
	encode,
	(err) => new Error("Error encoding HDR image", { cause: err }),
);

const reUseableRenderer = QuadRenderer.instantiateRenderer();

export const getDefaultEncodingOptions = Result.fromThrowable(
	(hdrTexture: DataTexture) => {
		const textureMinMax = findTextureMinMax(
			hdrTexture,
			"max",
			reUseableRenderer,
		);

		return {
			image: hdrTexture,
			// this will encode the full HDR range
			maxContentBoost: Math.max.apply(this, textureMinMax),
			renderer: reUseableRenderer,
		} as EncodingParametersBase;
	},
	(err) => new Error("Error finding HDR content boost min/max", { cause: err }),
);

const promiseWorker = getPromiseWorker(new GainMapWorker());
export const workerInterface = getWorkerInterface(promiseWorker);

const hdrLoader = new HDRLoader();
hdrLoader.setRequestHeader({
	Authorization: `token ${localStorage.getItem("oauth.access_token")}`,
});

const encodedCache: Record<
	string,
	ResultAsync<ReturnType<typeof encode>, Error>
> = {};

export const getEncodedHDR = (
	path: string,
	{ loader = hdrLoader }: { loader?: HDRLoader },
) => {
	encodedCache[path] ??= ResultAsync.fromPromise(
		loader.loadAsync(path),
		(err) => new Error(`Error loading HDR "${path}": ${err}`),
	)
		.andThen(getDefaultEncodingOptions)
		.andThen((options) => {
			const result = encodeThrowable(options);
			options.image.dispose();

			return result;
		});

	return encodedCache[path];
};

export const fileConvert = (content: Uint8Array) => {
	const file = new Blob([content], { type: "image/jpeg" });
	return { url: URL.createObjectURL(file), file };
};
